import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Star, ExternalLink } from 'lucide-react';
import { wishlistAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { getMarketplaceLabel } from '../../utils/marketplace';
import toast from 'react-hot-toast';

const ProductCard = ({ product }) => {
  const { isLoggedIn } = useAuth();
  const [wishlisted, setWishlisted] = useState(false);

  const image = product.images?.[0] || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400';
  const marketplace = getMarketplaceLabel(product.external_url);

  const handleWishlist = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) { toast.error('Please login first'); return; }
    try {
      await wishlistAPI.toggle({ product_id: product.product_id || product.id });
      setWishlisted(!wishlisted);
      toast.success(wishlisted ? 'Removed from wishlist' : 'Added to wishlist');
    } catch { toast.error('Failed'); }
  };

  const handleBuy = (e) => {
    e.preventDefault();
    if (!product.external_url) return;
    window.open(product.external_url, '_blank', 'noopener,noreferrer');
  };

  const discountPct = product.discount_percent ||
    (product.original_price ? Math.round(((product.original_price - product.price) / product.original_price) * 100) : 0);

  return (
    <Link to={`/products/${product.slug}`} style={{ textDecoration: 'none', display: 'block' }}>
      <div className="product-card" style={{ position: 'relative' }}>
        {/* Discount tag */}
        {discountPct > 0 && (
          <div className="discount-tag">{discountPct}% off</div>
        )}

        {/* Wishlist */}
        <button
          onClick={handleWishlist}
          style={{
            position: 'absolute', top: 10, right: 10,
            width: 32, height: 32, borderRadius: '50%',
            background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 2, border: 'none', cursor: 'pointer'
          }}
        >
          <Heart size={16} fill={wishlisted ? '#f43397' : 'none'} color={wishlisted ? '#f43397' : '#9ca3af'} />
        </button>

        {/* Image */}
        <div style={{ position: 'relative', overflow: 'hidden' }}>
          <img
            src={image}
            alt={product.name}
            className="product-card-img"
            style={{ transition: 'transform 0.3s ease' }}
            onError={e => { e.target.src = 'https://via.placeholder.com/300x400?text=No+Image'; }}
          />
        </div>

        <div className="product-card-body">
          <p className="product-card-name">{product.name}</p>

          {/* Rating */}
          {product.rating > 0 && (
            <div className="product-card-rating">
              <span style={{ background: '#22c55e', color: 'white', fontSize: 11, padding: '1px 6px', borderRadius: 4, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                {Number(product.rating).toFixed(1)} <Star size={9} fill="white" color="white" />
              </span>
              <span>({product.review_count || 0})</span>
            </div>
          )}

          {/* Price */}
          <div className="product-card-price" style={{ marginBottom: 8 }}>
            <span className="price-current">₹{Number(product.price).toLocaleString()}</span>
            {product.original_price && product.original_price > product.price && (
              <span className="price-original">₹{Number(product.original_price).toLocaleString()}</span>
            )}
            {discountPct > 0 && <span className="price-discount">{discountPct}% off</span>}
          </div>

          {/* Buy on external marketplace */}
          <button
            onClick={handleBuy}
            disabled={!product.external_url}
            style={{
              width: '100%', padding: '8px', borderRadius: 6, border: '1.5px solid #f43397',
              background: product.external_url ? 'white' : '#f3f4f6',
              color: product.external_url ? '#f43397' : '#9ca3af',
              borderColor: product.external_url ? '#f43397' : '#e5e7eb',
              fontSize: 13, fontWeight: 600, cursor: product.external_url ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'all 0.2s', fontFamily: 'inherit'
            }}
            onMouseEnter={e => { if (product.external_url) { e.currentTarget.style.background = '#f43397'; e.currentTarget.style.color = 'white'; }}}
            onMouseLeave={e => { e.currentTarget.style.background = product.external_url ? 'white' : '#f3f4f6'; e.currentTarget.style.color = product.external_url ? '#f43397' : '#9ca3af'; }}
          >
            <ExternalLink size={14} />
            {product.external_url ? `Buy on ${marketplace}` : 'Link coming soon'}
          </button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
